import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import * as uploadApi from '../../helpers/api/uploadApi';
import { track } from '../../helpers/analytics';
import {
  guideWriteCommandApi,
  placeAssetInImageWidget,
  setImageActionApi,
} from '../../helpers/api/guidelinesApi';
import { GUIDE_KEYS } from '../../queries/guides';
import {
  CommandImageWidget,
  ImageWidget,
  Page,
  StyleSheet,
  WIDGET_TYPE,
} from '../../types';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { optimisticallyUpdateWidget } from '../helpers';
import { Command } from '../../types/commands';
import { queryClient } from '../../constants/app';
import { ImageActions } from '../../constants/widgets';
import { useGuideStore } from '../../stores/guideStore';
import { useUploaderStore } from '../../stores/uploaderStore';
import { optimisticallyUpdateWidgetStyle } from './collections';
import { getEditImageWidgetStyleCommand } from '../../helpers/api/commands/widgets/imageWidgetCommands';

export const useImageWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId?: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;
  const clearUploadingImage = useUploaderStore.use.clearUploadingImage();

  return {
    updateImageWidget: useMutation(
      (options: CommandImageWidget) => {
        let wgtId = widgetId;

        if (!wgtId) {
          wgtId = sessionStorage.getItem('newWidgetId') ?? '';
        }

        return placeAssetInImageWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          wgtId ?? '',
          WIDGET_TYPE.IMAGE,
          options,
          activeGuideVersion,
        );
      },
      {
        onMutate: variables => {
          let wgtId = widgetId;

          if (!wgtId) {
            wgtId = sessionStorage.getItem('newWidgetId') ?? '';
          }

          if (!wgtId) {
            throw new Error('Widget ID is missing');
          }

          updateGuideVersion(activeGuideId);
          optimisticallyUpdateWidget(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            wgtId ?? '',
            WIDGET_TYPE.IMAGE,
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Image Uploaded', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    uploadImageWidget: useMutation(
      (options: {
        assetId: string;
        widgetId: string;
        sectionId: string;
        columnIndex: number;
      }) => {
        return uploadApi.retrieveAssetInfoApi(options.assetId);
      },
      {
        retry: true,
        retryDelay: 2000,
        onSuccess: (data, variables) => {
          clearUploadingImage(variables.widgetId);
          updateGuideVersion(activeGuideId);
          const imageAsset: CommandImageWidget = {
            widgetAssetId: data.id,
            widgetAssetName: data.name,
            widgetImageUrl: data.original,
            widgetDetailUrl: '',
            widgetAssetExtension: data.extension[0],
          };
          optimisticallyUpdateWidget(
            activeGuideId,
            activePageId,
            variables.sectionId,
            variables.columnIndex,
            variables.widgetId,
            WIDGET_TYPE.PERSONAL_FILE,
            imageAsset,
          );

          placeAssetInImageWidget(
            activeGuideId,
            activeChapterId,
            activePageId,
            variables.sectionId,
            variables.widgetId,
            WIDGET_TYPE.IMAGE,
            imageAsset,
            activeGuideVersion,
          );
          track('Media uploaded from media widget');
        },
      },
    ),
    editStyle: useMutation(
      (options: { style: string }) => {
        const command = getEditImageWidgetStyleCommand({
          aggregateId: activeGuideId,
          aggregateVersion: activeGuideVersion,
          chapterId: activeChapterId,
          sectionId,
          columnIndex,
          widgetId,
          pageId: activePageId,
          widgetStyle: {
            stylesheet: options.style,
            language: 'css',
            version: 3,
          } as StyleSheet,
        });
        return guideWriteCommandApi(command);
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateWidgetStyle(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.style,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    setImageAction: useMutation(
      (options: {
        imageAction: ImageActions;
        imageActionUrl?: string;
        imageActionNewTab?: boolean;
      }) => {
        const actionCommand = {
          [ImageActions.OPEN_PREVIEW]: Command.SET_IMAGE_ACTION_TO_OPEN_PREVIEW,
          [ImageActions.NO_ACTION]: Command.SET_IMAGE_ACTION_TO_NO_ACTION,
          [ImageActions.LINK_TO_URL]: Command.SET_IMAGE_ACTION_TO_LINK_TO_URL,
        };

        if (options.imageAction === ImageActions.LINK_TO_URL) {
          return setImageActionApi(
            activeGuideId,
            activeChapterId,
            activePageId,
            sectionId,
            widgetId ?? '',
            columnIndex,
            actionCommand[options.imageAction],
            activeGuideVersion,
            options.imageActionUrl || '',
            options.imageActionNewTab || false,
          );
        }

        return setImageActionApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId ?? '',
          columnIndex,
          actionCommand[options.imageAction],
          activeGuideVersion,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallySetImageAction(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId ?? '',
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Image Widget Edited', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallySetImageAction = (
  guideId: string,
  pageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  payload: {
    imageAction: ImageActions;
    imageActionNewTab?: boolean;
    imageActionUrl?: string;
  },
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );
      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );
      const newWidget = {
        ...old.sections[sectionIndex].columns[columnIndex].widgets[widgetIndex],
        imageAction: {
          type: payload.imageAction,
          openNewTab: payload.imageActionNewTab,
          url: payload.imageActionUrl,
        },
      } as ImageWidget;

      if (old) {
        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = newWidget;
        });
      }

      return old;
    },
  );
};
